import { useEffect, useState } from "react";
import { ShieldCheck, Check, X, RotateCcw, MessageSquarePlus, ChevronLeft, FileText, ExternalLink, Mail, MessageCircle } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_LABEL = {
  documents_required: "Documents Required", documents_submitted: "Documents Submitted", ai_review: "AI Review",
  admin_review: "Admin Review", additional_info_required: "Additional Info", approved: "Approved",
  rejected: "Rejected", suspended: "Suspended", expired: "Expired", reverification_required: "Re-verify",
};
const FILTERS = ["admin_review", "additional_info_required", "approved", "rejected", "suspended", "reverification_required", "all"];
const badge = (s) => s === "approved" ? "bg-emerald-100 text-emerald-700"
  : ["rejected", "suspended", "expired", "replacement_required"].includes(s) ? "bg-rose-100 text-rose-700"
  : "bg-amber-100 text-amber-700";

const COMMISSION_BADGE = {
  accepted: { label: "Commission accepted", cls: "bg-emerald-100 text-emerald-700" },
  not_accepted: { label: "Commission not accepted", cls: "bg-amber-100 text-amber-700" },
  none: { label: "No commission set", cls: "bg-slate-100 text-slate-500" },
};
const CommissionBadge = ({ commission, testid }) => {
  const c = COMMISSION_BADGE[commission?.state] || COMMISSION_BADGE.none;
  const title = commission?.state === "accepted" && commission?.accepted_at
    ? `Accepted ${String(commission.accepted_at).slice(0, 10)}` : c.label;
  return <span data-testid={testid} title={title} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${c.cls}`}>{c.label}</span>;
};

// Expiry status badge from the (AI-read) document expiry date — helps the admin approve faster.
const docValidity = (expiry) => {
  if (!expiry) return null;
  const d = new Date(String(expiry).slice(0, 10));
  if (isNaN(d.getTime())) return null;
  const days = Math.ceil((d - new Date()) / 86400000);
  if (days < 0) return { label: "Expired", cls: "bg-rose-100 text-rose-700", days };
  if (days <= 30) return { label: `Expiring · ${days}d`, cls: "bg-amber-100 text-amber-700", days };
  return { label: "Valid", cls: "bg-emerald-100 text-emerald-700", days };
};
const ExpiryBadge = ({ expiry, testid }) => {
  const v = docValidity(expiry);
  if (!v) return <span data-testid={testid} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-400" title="No expiry on document">No expiry</span>;
  return <span data-testid={testid} title={`Valid till ${String(expiry).slice(0, 10)}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${v.cls}`}>{v.label}</span>;
};

export function AdminSellerVerification() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("admin_review");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get(`/admin/seller-applications?status=${filter}`).then((r) => setList(r.data.applications || [])).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const open = (a) => api.get(`/admin/seller-applications/${a.seller_id}`).then((r) => setSel(r.data)).catch(() => {});

  const docDecision = async (docId, decision) => {
    const comment = decision !== "approve" ? prompt("Comment for the seller (optional):") : null;
    try { await api.post(`/admin/seller-applications/${sel.application.seller_id}/documents/${docId}/decision`, { decision, comment });
      toast.success("Document updated"); open({ seller_id: sel.application.seller_id }); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const requestInfo = async () => {
    const message = prompt("What additional information do you need from the seller?");
    if (!message) return;
    try { await api.post(`/admin/seller-applications/${sel.application.seller_id}/request-info`, { message });
      toast.success("Requested"); open({ seller_id: sel.application.seller_id }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const decide = async (decision) => {
    const reason = decision !== "approve" ? prompt("Reason (shown to seller):") : null;
    if (decision !== "approve" && reason === null) return;
    try { await api.post(`/admin/seller-applications/${sel.application.seller_id}/decision`, { decision, reason, admin_note: note || null });
      toast.success(`Seller ${decision}d`); setNote(""); open({ seller_id: sel.application.seller_id }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const catDecision = async (category_id, decision) => {
    const cnote = decision === "reject" ? prompt("Reason (shown to seller):") : null;
    if (decision === "reject" && cnote === null) return;
    try { await api.post(`/admin/seller-applications/${sel.application.seller_id}/category-decision`, { category_id, decision, note: cnote });
      toast.success(`Category ${decision}d`); open({ seller_id: sel.application.seller_id }); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendCommissionReminder = async () => {
    try { await api.post(`/admin/seller-applications/${sel.application.seller_id}/commission-reminder`);
      toast.success("Reminder email sent to seller"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDoc = (d) => {
    if (d.scan_status === "quarantined") return;
    window.open(`${API}/admin/seller-applications/${sel.application.seller_id}/documents/${d.document_id}/file`, "_blank", "noopener,noreferrer");
  };

  if (sel) {
    const a = sel.application; const docs = sel.documents || [];
    return (
      <div data-testid="admin-seller-detail">
        <button onClick={() => setSel(null)} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft className="h-4 w-4" /> Back to list</button>
        <PageHeader title={a.seller_name} icon={ShieldCheck} subtitle={`Status: ${STATUS_LABEL[a.status] || a.status} · ${sel.seller?.email || ""}`} />

        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="admin-seller-commission">
          <CommissionBadge commission={sel.commission} testid="admin-seller-commission-badge" />
          {sel.commission?.state === "not_accepted" && (
            <button onClick={sendCommissionReminder} data-testid="admin-commission-reminder-btn" className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100"><Mail className="h-3.5 w-3.5" /> Email seller to accept commission</button>
          )}
        </div>


        {(sel.seller?.email || a.phone) && (
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="admin-seller-contact">
            <span className="text-xs font-semibold text-slate-500">Contact seller:</span>
            {sel.seller?.email && <a href={`mailto:${sel.seller.email}`} data-testid="admin-seller-email-link" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-blue-300"><Mail className="h-3.5 w-3.5 text-blue-500" /> {sel.seller.email}</a>}
            {a.phone && <a href={`https://wa.me/${String(a.phone).replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid="admin-seller-whatsapp-link" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp {a.phone}</a>}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="card-soft p-4 lg:col-span-1">
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Business</h3>
            <dl className="space-y-1 text-xs text-slate-500">
              {[["Legal name", a.legal_name], ["Type", a.business_type], ["Contact", a.contact_person], ["Phone", a.phone], ["PAN", a.pan], ["GST", a.gst], ["Address", a.address], ["State", a.state], ["District", a.district], ["Pincode", a.pincode]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2"><dt className="text-slate-400">{k}</dt><dd className="text-right font-medium text-slate-700">{v}</dd></div>
              ))}
            </dl>
            <h3 className="mb-1 mt-3 text-sm font-semibold text-slate-700">Wants to sell</h3>
            <p className="text-xs text-slate-500">{a.intended_products || "—"}</p>
            <p className="mt-1 text-xs text-slate-400">Detected: {(a.detected_categories || []).join(", ") || "general"}</p>
            {(a.seller_categories || []).length > 0 && (
              <div className="mt-3" data-testid="admin-seller-categories">
                <h3 className="mb-1 text-sm font-semibold text-slate-700">Selling categories (per-category approval)</h3>
                {a.seller_categories.map((c) => (
                  <div key={c.category_id} className="flex items-center justify-between gap-2 py-1" data-testid={`admin-seller-cat-${c.category_id}`}>
                    <span className="min-w-0 flex-1 truncate text-xs text-slate-600">{c.name}
                      <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold capitalize ${c.status === "approved" ? "bg-emerald-100 text-emerald-700" : c.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{c.status}</span></span>
                    <span className="flex shrink-0 gap-1">
                      <button onClick={() => catDecision(c.category_id, "approve")} data-testid={`admin-seller-cat-approve-${c.category_id}`} className="rounded-lg bg-emerald-500 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-emerald-600">Approve</button>
                      <button onClick={() => catDecision(c.category_id, "reject")} data-testid={`admin-seller-cat-reject-${c.category_id}`} className="rounded-lg border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:border-rose-300 hover:text-rose-600">Reject</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-3">
            <div className="card-soft p-4">
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Documents ({docs.length})</h3>
              {docs.length === 0 && <p className="text-sm text-slate-400">No documents uploaded yet.</p>}
              <div className="space-y-2">
                {docs.map((d) => (
                  <div key={d.document_id} className="rounded-lg border border-slate-100 p-3" data-testid={`admin-doc-${d.code}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      {d.scan_status === "quarantined" ? (
                        <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-300" /><div>
                          <p className="text-sm font-medium text-slate-500">{d.name} <span className="text-xs text-slate-400">({d.filename})</span></p>
                          <p className="text-[11px] text-rose-500">Blocked by security scan — no file to view</p>
                        </div></div>
                      ) : (
                        <button type="button" onClick={() => openDoc(d)} data-testid={`admin-doc-open-${d.code}`}
                          title="Open document in a new tab"
                          className="group flex items-center gap-2 text-left">
                          <FileText className="h-4 w-4 text-slate-400 group-hover:text-blue-500" /><div>
                          <p className="text-sm font-medium text-slate-800 group-hover:text-blue-600 group-hover:underline inline-flex items-center gap-1">{d.name} <span className="text-xs text-slate-400">({d.filename})</span> <ExternalLink className="h-3 w-3 text-slate-400 group-hover:text-blue-500" /></p>
                          <p className="text-[11px] text-slate-400">Scan: {d.scan_status} · {d.expiry_date ? `Expires ${d.expiry_date}` : "No expiry"}</p>
                        </div></button>
                      )}
                      <span className="flex shrink-0 items-center gap-1.5">
                        <ExpiryBadge expiry={d.expiry_date} testid={`admin-doc-expiry-${d.code}`} />
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge(d.status)}`}>{d.status}</span>
                      </span>
                    </div>
                    {d.ai_findings?.length > 0 && <p className="mt-1 text-xs text-amber-600">AI findings: {d.ai_findings.join("; ")}</p>}
                    {d.extracted && Object.values(d.extracted).some(Boolean) && <p className="mt-1 text-[11px] text-slate-400">Extracted: {Object.entries(d.extracted).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>}
                    {d.scan_status === "clean" && (
                      <div className="mt-2 flex gap-2">
                        <button onClick={() => docDecision(d.document_id, "approve")} data-testid={`admin-doc-approve-${d.code}`} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-600"><Check className="h-3 w-3" /> Approve</button>
                        <button onClick={() => docDecision(d.document_id, "reject")} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-rose-300 hover:text-rose-600"><X className="h-3 w-3" /> Reject</button>
                        <button onClick={() => docDecision(d.document_id, "replace")} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-amber-300"><RotateCcw className="h-3 w-3" /> Request replacement</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="card-soft p-4">
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Decision</h3>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Private admin note (optional)" className="mb-3" data-testid="admin-note" />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => decide("approve")} data-testid="admin-approve-seller" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"><Check className="h-4 w-4" /> Approve Seller</button>
                <button onClick={() => decide("reject")} data-testid="admin-reject-seller" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:border-rose-300 hover:text-rose-600"><X className="h-4 w-4" /> Reject</button>
                <button onClick={() => decide("suspend")} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:border-rose-300">Suspend</button>
                <button onClick={() => decide("reverify")} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600">Re-verify</button>
                <button onClick={requestInfo} data-testid="admin-request-info" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:border-blue-300"><MessageSquarePlus className="h-4 w-4" /> Request info</button>
              </div>
            </div>

            {a.history?.length > 0 && (
              <div className="card-soft p-4">
                <h3 className="mb-2 text-sm font-semibold text-slate-700">History</h3>
                <ul className="space-y-1 text-xs text-slate-500">
                  {a.history.slice().reverse().map((h, i) => (<li key={i}>{h.at?.slice(0, 16).replace("T", " ")} · {h.action} {h.before ? `(${h.before}→${h.after})` : ""} · {h.by}</li>))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-seller-verification">
      <PageHeader title="Seller Verification" icon={ShieldCheck} subtitle="Review seller applications, documents and AI findings, then approve or reject." />
      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`} className={`rounded-full px-3 py-1.5 text-xs font-medium ${filter === f ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{f === "all" ? "All" : STATUS_LABEL[f] || f}</button>
        ))}
      </div>
      <div className="card-soft divide-y divide-slate-50">
        {list.length === 0 && <p className="p-6 text-center text-sm text-slate-400">No applications in this view.</p>}
        {list.map((a) => (
          <button key={a.seller_id} onClick={() => open(a)} data-testid="admin-seller-row" className="flex w-full items-center justify-between p-3 text-left hover:bg-slate-50">
            <div>
              <p className="text-sm font-semibold text-slate-800">{a.seller_name}</p>
              <p className="text-xs text-slate-400">Wants: {a.intended_products || "—"} · updated {a.updated_at?.slice(0, 10)}</p>
            </div>
            <span className="flex shrink-0 items-center gap-2">
              <CommissionBadge commission={a.commission} testid="admin-seller-row-commission" />
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${badge(a.status)}`}>{STATUS_LABEL[a.status] || a.status}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
